/**
 * The result, as an OVERLAY on the board column — not a Rail body.
 *
 * Why: replacing the Rail with a result card threw the move list away at the
 * exact moment the player wants to scroll back through it. So the Rail stays
 * in its live layout (moves + dock + toolbar with the game actions disabled)
 * and the result floats over the board, which is still visible behind a dim
 * backdrop. The × dismisses the card and leaves the final position and the
 * notation on screen for browsing; the next game clears the flag.
 *
 * Measures from design `05 Result · won` / `05b Result · variants`:
 *   outcome word  display 92 / line-height 1 / letter-spacing 0.02em
 *   message       17 / 600
 *   end-type line 14 / ink2
 *   rating card   flat card 14 16, kicker 11, value display 56, delta 40
 *   players row   avatar + name 700 + colour 12, centre score display 30
 *   buttons       height 48, Rematch flex 1.3, the others flex 1
 *
 * M4 adds three arms, all under the result line and above the rating card:
 *  - SWISS: the old modal's "standings are updated every 30 seconds" line and a
 *    "Go to tournament" CTA;
 *  - ARENA: the 🔥 streak and ⚡ berserk chips, the "Next pairing in Ns" line
 *    (its own ticking leaf, `NextPairing`), "Go to tournament" and a two-cell row
 *    of "Stay here" and Resume/Pause. This repo's old `ResultComponent` has NO
 *    arena block — it is a deliberate improvement taken from the app lane
 *    (`PARITY.md` M4);
 *  - SPECTATOR: a watched game's end. No ×, no rating card, no seats of mine, no
 *    Review and no play-again row — one "Back to tournament" button, because
 *    leaving the watch is its only exit.
 */
import React, {useEffect, useRef} from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';

import {RADIUS, SIZE, SPACE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {Button, Chip, Kicker, Txt} from '../../ui';
import DeltaCounterView from './DeltaCounter.View';
import NextPairing from './NextPairing';
import SeatView from './Seat.View';
import {ResultOverlayModel} from './model';

/** the card rises 12 px over 320 ms while the scrim fades in over 180 (UX §7) */
const RISE_FROM = 12;
const RISE_MS = 320;
/** Rematch is the widest of the three footer buttons (05b) */
const REMATCH_FLEX = 1.3;

export default React.memo(function ResultOverlayView({
  result,
  visible,
  delta,
  canReview,
  showRating,
  canPlayAgain,
  canRematch,
  myColor,
  opponentColor,
  section,
  spectator,
  headline,
  score,
  line,
  arenaStreak,
  arenaBerserked,
  arenaPaused,
  onClose,
  newGameLabel,
  onNewGame,
  onReview,
  onRematch,
  onGoToTournament,
  onArenaPauseToggle,
  onStopWatching,
}: ResultOverlayModel) {
  const tokens = useV2Theme(s => s.tokens);
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(RISE_FROM)).current;
  const key = result?.resultKey ?? '';

  useEffect(() => {
    if (!visible) {
      return;
    }
    fade.setValue(0);
    rise.setValue(RISE_FROM);
    Animated.parallel([
      Animated.timing(fade, {
        toValue: 1,
        duration: 180,
        easing: Easing.bezier(0.2, 0.8, 0.3, 1),
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(rise, {
        toValue: 0,
        duration: RISE_MS,
        easing: Easing.bezier(0.2, 0.8, 0.3, 1),
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();
    // `key` re-runs the reveal for a second result in the same mount
  }, [visible, key, fade, rise]);

  if (!result || !visible) {
    return null;
  }

  const deltaColor =
    delta === 'pos'
      ? tokens.pos
      : delta === 'danger'
      ? tokens.danger
      : tokens.muted;

  return (
    <View style={styles.layer} pointerEvents="box-none">
      {/* backdrop and card are SIBLINGS: animating one View's opacity would
          dim the card along with the board behind it. The scrim carries its own
          alpha (tokens.overlayScrim), so this is a plain 0 -> 1 fade. */}
      <Animated.View
        pointerEvents="auto"
        style={[
          styles.backdrop,
          {backgroundColor: tokens.overlayScrim, opacity: fade},
        ]}
      />
      <Animated.View
        style={[
          styles.card,
          {
            backgroundColor: tokens.surface,
            borderColor: tokens.cardline,
            boxShadow: tokens.sh2,
            opacity: fade,
            transform: [{translateY: rise}],
          } as any,
        ]}>
        {!spectator && (
          <Pressable
            onPress={onClose}
            accessibilityLabel="Dismiss the result"
            style={({hovered}: any) => [
              styles.close,
              {
                boxShadow: `inset 0 0 0 1px ${tokens.line2}`,
                backgroundColor: hovered ? tokens.well : 'transparent',
              } as any,
            ]}>
            <Txt variant="closeGlyph" tone="ink2">
              ×
            </Txt>
          </Pressable>
        )}

        <Txt variant="outcome" numeric>
          {headline}
        </Txt>
        <Txt variant="message">{result.message}</Txt>
        {!spectator && (
          <Txt variant="bodyLg" tone="ink2" style={styles.line}>
            {line}
          </Txt>
        )}

        {section === 'swiss' && (
          <Txt
            testID="v2-result-swiss"
            variant="bodyLg"
            tone="ink2"
            style={styles.line}>
            Standings are updated every 30 seconds.
          </Txt>
        )}

        {section === 'arena' && (
          <View style={styles.arenaBlock} testID="v2-result-arena">
            {(arenaStreak > 0 || arenaBerserked) && (
              <View style={styles.chips}>
                {arenaStreak > 0 && <Chip label={`🔥 Streak ${arenaStreak}`} />}
                {arenaBerserked && <Chip label="⚡ Berserk" />}
              </View>
            )}
            <NextPairing paused={arenaPaused} />
          </View>
        )}

        {showRating && (
          <View
            style={[
              styles.ratingCard,
              {
                backgroundColor: tokens.well,
                boxShadow: `inset 0 0 0 1px ${tokens.line}`,
              } as any,
            ]}>
            <Kicker text="RATING" />
            <View style={styles.ratingRow}>
              <Txt variant="rating" numeric>
                {result.myRating ?? '–'}
              </Txt>
              <DeltaCounterView
                delta={result.myRatingDelta}
                color={deltaColor}
              />
            </View>
          </View>
        )}

        {!spectator && (
          <View style={styles.seats}>
            <SeatView
              name={result.myName}
              colorLabel={myColor}
              mine
              align="left"
            />
            <Txt variant="score" numeric>
              {score || '–'}
            </Txt>
            <SeatView
              name={result.opponentName}
              colorLabel={opponentColor}
              mine={false}
              align="right"
            />
          </View>
        )}

        {spectator ? (
          <View style={styles.footer}>
            <Button
              label="Back to tournament"
              size="lg"
              flex={1}
              onPress={onStopWatching}
            />
          </View>
        ) : (
          <>
            <View style={styles.footer}>
              {canRematch && (
                <Button
                  label="Rematch"
                  variant="outline"
                  size="lg"
                  flex={REMATCH_FLEX}
                  onPress={onRematch}
                />
              )}
              {canReview && (
                <Button
                  label="Review"
                  icon="review"
                  variant="outline"
                  size="lg"
                  flex={1}
                  onPress={onReview}
                />
              )}
              {canPlayAgain && (
                <Button
                  label={newGameLabel}
                  size="lg"
                  flex={1}
                  onPress={onNewGame}
                />
              )}
            </View>

            {section !== 'none' && (
              <View style={styles.footer}>
                <Button
                  label="Go to tournament"
                  size="lg"
                  flex={section === 'arena' ? REMATCH_FLEX : 1}
                  onPress={onGoToTournament}
                />
                {section === 'arena' && (
                  <>
                    <Button
                      label="Stay here"
                      variant="outline"
                      size="lg"
                      flex={1}
                      onPress={onClose}
                    />
                    <Button
                      label={arenaPaused ? 'Resume' : 'Pause'}
                      variant="outline"
                      size="lg"
                      flex={1}
                      onPress={onArenaPauseToggle}
                    />
                  </>
                )}
              </View>
            )}
          </>
        )}
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  layer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // R6.1: 12 px, the same radius as the board frame it dims
    borderRadius: RADIUS.panel,
  },
  card: {
    width: '92%',
    maxWidth: SIZE.resultCardMax,
    borderRadius: RADIUS.panel,
    borderWidth: 1,
    paddingVertical: 22,
    paddingHorizontal: SPACE.xxl,
    gap: SPACE.sm,
  },
  close: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  line: {
    marginBottom: 6,
  },
  ratingCard: {
    paddingVertical: 14,
    paddingHorizontal: SPACE.lg,
    borderRadius: RADIUS.panel,
    gap: 6,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: SPACE.md,
  },
  seats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.md,
    marginTop: 6,
  },
  footer: {
    flexDirection: 'row',
    gap: SPACE.sm,
    marginTop: 10,
  },
  /** M4: the arena chips + countdown, centred under the result line */
  arenaBlock: {
    alignItems: 'center',
    gap: SPACE.sm,
    marginBottom: 6,
  },
  chips: {
    flexDirection: 'row',
    gap: SPACE.sm,
  },
});
